import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FaBuilding, FaCalendarAlt, FaCheckCircle } from "react-icons/fa";
import { Company } from "../types/company";
import { ApplicationWithCompany } from "../types/application";
import { ScheduleWithCompany } from "../types/schedule";
import { ApiResponse, CompanyApiResponse } from "../types/auth";
import { ScheduleApiResponse } from "../types/schedule";

interface StatusCount {
  applied: number;
  document_screening: number;
  interview_scheduled: number;
  final_interview: number;
  offer: number;
  rejected: number;
  declined: number;
}

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [companiesCount, setCompaniesCount] = useState<number>(0);
  const [applicationsCount, setApplicationsCount] = useState<number>(0);
  const [schedulesCount, setSchedulesCount] = useState<number>(0);
  const [todaySchedules, setTodaySchedules] = useState<ScheduleWithCompany[]>(
    []
  );
  const [upcomingSchedules, setUpcomingSchedules] = useState<
    ScheduleWithCompany[]
  >([]);
  const [statusCounts, setStatusCounts] = useState<StatusCount>({
    applied: 0,
    document_screening: 0,
    interview_scheduled: 0,
    final_interview: 0,
    offer: 0,
    rejected: 0,
    declined: 0,
  });
  const [loading, setLoading] = useState<boolean>(true);

  const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5173";

  // データを取得
  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);

      // 企業数を取得
      const companiesResponse = await fetch(`${API_BASE_URL}/api/companies`, {
        method: "GET",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });

      if (companiesResponse.ok) {
        const companiesData: ApiResponse<CompanyApiResponse> =
          await companiesResponse.json();
        const companies: Company[] = Array.isArray(companiesData.data)
          ? companiesData.data
          : companiesData.data?.companies || [];
        setCompaniesCount(companies.length);
      }

      // 応募状況を取得
      const applicationsResponse = await fetch(
        `${API_BASE_URL}/api/applications`,
        {
          method: "GET",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
        }
      );

      if (applicationsResponse.ok) {
        const applicationsData: ApiResponse<ApplicationWithCompany[]> =
          await applicationsResponse.json();
        const applications = applicationsData.data || [];
        setApplicationsCount(applications.length);

        // ステータスごとの集計
        const counts: StatusCount = {
          applied: 0,
          document_screening: 0,
          interview_scheduled: 0,
          final_interview: 0,
          offer: 0,
          rejected: 0,
          declined: 0,
        };

        applications.forEach((app) => {
          if (app.status in counts) {
            counts[app.status as keyof StatusCount]++;
          }
        });

        setStatusCounts(counts);
      }

      // スケジュール数を取得
      const schedulesResponse = await fetch(`${API_BASE_URL}/api/schedules`, {
        method: "GET",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });

      if (schedulesResponse.ok) {
        const schedulesData: ApiResponse<ScheduleApiResponse> =
          await schedulesResponse.json();

        // 柔軟に受け付ける
        const payload = schedulesData.data;
        let schedules: ScheduleWithCompany[] = [];

        if (Array.isArray(payload)) {
          schedules = payload;
        } else if (payload && Array.isArray((payload as any).schedules)) {
          schedules = (payload as any).schedules;
        }

        setSchedulesCount(schedules.length);

        const now = new Date();
        const today = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate()
        );
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        const oneWeekLater = new Date();
        oneWeekLater.setDate(now.getDate() + 7);

        // 今日のスケジュールをフィルタリング
        const todayEvents = schedules
          .filter((schedule) => {
            const startDate = new Date(schedule.schedule_start_date);
            return startDate >= today && startDate < tomorrow;
          })
          .sort(
            (a, b) =>
              new Date(a.schedule_start_date).getTime() -
              new Date(b.schedule_start_date).getTime()
          )
          .slice(0, 5); // 最大5件

        setTodaySchedules(todayEvents);

        // 今週のスケジュールをフィルタリング（明日から7日間）
        const upcoming = schedules
          .filter((schedule) => {
            const startDate = new Date(schedule.schedule_start_date);
            return startDate >= tomorrow && startDate <= oneWeekLater;
          })
          .sort(
            (a, b) =>
              new Date(a.schedule_start_date).getTime() -
              new Date(b.schedule_start_date).getTime()
          )
          .slice(0, 5); // 最大5件

        setUpcomingSchedules(upcoming);
      }
    } catch (error) {
      // エラーハンドリング
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-16 text-gray-600">
        <div className="loading-spinner"></div>
        <span>読み込み中...</span>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-7xl mx-auto p-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 transition-all hover:-translate-y-0.5 hover:shadow-lg">
            <div className="text-4xl mb-4 text-indigo-500">
              <FaBuilding />
            </div>
            <h3 className="text-xl font-semibold text-gray-800 m-0 mb-2">
              企業管理
            </h3>
            <p className="text-gray-600 text-sm m-0 mb-4 leading-relaxed">
              気になる企業の情報を登録・管理
            </p>
            <div className="flex items-baseline gap-2 mb-4">
              <span className="text-3xl font-bold text-indigo-500">
                {companiesCount}
              </span>
              <span className="text-xs text-gray-400 uppercase tracking-wider">
                登録企業
              </span>
            </div>
            <button
              className="w-full py-3 px-4 bg-indigo-500 text-white border-none rounded-md text-sm font-medium cursor-pointer transition-colors hover:bg-indigo-600"
              onClick={() => navigate("/companies")}
            >
              企業を確認
            </button>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 transition-all hover:-translate-y-0.5 hover:shadow-lg">
            <div className="text-4xl mb-4 text-green-500">
              <FaCheckCircle />
            </div>
            <h3 className="text-xl font-semibold text-gray-800 m-0 mb-2">
              応募状況
            </h3>
            <p className="text-gray-600 text-sm m-0 mb-4 leading-relaxed">
              エントリー状況と選考進捗を追跡
            </p>
            <div className="flex items-baseline gap-2 mb-4">
              <span className="text-3xl font-bold text-green-500">
                {applicationsCount}
              </span>
              <span className="text-xs text-gray-400 uppercase tracking-wider">
                応募中
              </span>
            </div>
            <button
              className="w-full py-3 px-4 bg-green-500 text-white border-none rounded-md text-sm font-medium cursor-pointer transition-colors hover:bg-green-600"
              onClick={() => navigate("/companies")}
            >
              応募状況を確認
            </button>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 transition-all hover:-translate-y-0.5 hover:shadow-lg">
            <div className="text-4xl mb-4 text-orange-500">
              <FaCalendarAlt />
            </div>
            <h3 className="text-xl font-semibold text-gray-800 m-0 mb-2">
              スケジュール
            </h3>
            <p className="text-gray-600 text-sm m-0 mb-4 leading-relaxed">
              面接やES締切日を管理
            </p>
            <div className="flex items-baseline gap-2 mb-4">
              <span className="text-3xl font-bold text-orange-500">
                {schedulesCount}
              </span>
              <span className="text-xs text-gray-400 uppercase tracking-wider">
                予定
              </span>
            </div>
            <button
              className="w-full py-3 px-4 bg-orange-500 text-white border-none rounded-md text-sm font-medium cursor-pointer transition-colors hover:bg-orange-600"
              onClick={() => navigate("/schedules")}
            >
              スケジュールを確認
            </button>
          </div>
        </div>

        {/* 今日のタスク */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <FaCalendarAlt className="text-2xl text-yellow-500" />
            <h2 className="text-2xl font-bold text-gray-800 m-0">
              今日のタスク
            </h2>
          </div>

          {todaySchedules.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              今日の予定はありません
            </div>
          ) : (
            <div className="space-y-3">
              {todaySchedules.map((schedule) => {
                const startDate = new Date(schedule.schedule_start_date);

                const getScheduleTypeLabel = (type: string): string => {
                  const typeMap: { [key: string]: string } = {
                    es_deadline: "ES締切",
                    internship: "インターン",
                    interview: "面接",
                    screening: "グループディスカッション",
                    orientation: "説明会",
                    other: "その他",
                  };
                  return typeMap[type] || type;
                };

                const getScheduleTypeColor = (type: string): string => {
                  const colorMap: { [key: string]: string } = {
                    es_deadline: "bg-red-100 text-red-800 border-red-200",
                    internship: "bg-blue-100 text-blue-800 border-blue-200",
                    interview:
                      "bg-orange-100 text-orange-800 border-orange-200",
                    screening:
                      "bg-purple-100 text-purple-800 border-purple-200",
                    orientation: "bg-teal-100 text-teal-800 border-teal-200",
                    other: "bg-gray-100 text-gray-800 border-gray-200",
                  };
                  return (
                    colorMap[type] ||
                    "bg-gray-100 text-gray-800 border-gray-200"
                  );
                };

                return (
                  <div
                    key={schedule.id}
                    className="p-4 rounded-lg border-2 bg-yellow-50 border-yellow-400 transition-all hover:shadow-md cursor-pointer"
                    onClick={() => navigate("/schedules")}
                  >
                    <div className="flex items-start gap-3 flex-col sm:flex-row">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <span
                            className={`px-3 py-1 rounded-full text-sm font-medium border ${getScheduleTypeColor(
                              schedule.schedule_type
                            )}`}
                          >
                            {getScheduleTypeLabel(schedule.schedule_type)}
                          </span>
                          <span className="px-3 py-1 rounded-full text-sm font-bold bg-yellow-400 text-yellow-900">
                            今日
                          </span>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-800 m-0 mb-1">
                          {schedule.company_name}
                        </h3>
                        <p className="text-sm text-gray-600 m-0">
                          {startDate.toLocaleString("ja-JP", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </p>
                      </div>
                      <button
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate("/schedules");
                        }}
                      >
                        詳細を見る
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {todaySchedules.length > 0 && (
            <div className="mt-6 text-center">
              <button
                className="px-6 py-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors font-medium"
                onClick={() => navigate("/schedules")}
              >
                すべてのスケジュールを見る →
              </button>
            </div>
          )}
        </div>

        {/* 今週の予定（明日以降） */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 mb-12">
          <div className="flex items-center gap-3 mb-6">
            <FaCalendarAlt className="text-2xl text-blue-500" />
            <h2 className="text-2xl font-bold text-gray-800 m-0">今週の予定</h2>
          </div>

          {upcomingSchedules.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              今週の予定はありません
            </div>
          ) : (
            <div className="space-y-3">
              {upcomingSchedules.map((schedule) => {
                const startDate = new Date(schedule.schedule_start_date);
                const isToday =
                  startDate.toDateString() === new Date().toDateString();
                const isTomorrow =
                  startDate.toDateString() ===
                  new Date(Date.now() + 86400000).toDateString();

                const getScheduleTypeLabel = (type: string): string => {
                  const typeMap: { [key: string]: string } = {
                    es_deadline: "ES締切",
                    internship: "インターン",
                    interview: "面接",
                    screening: "グループディスカッション",
                    orientation: "説明会",
                    other: "その他",
                  };
                  return typeMap[type] || type;
                };

                const getScheduleTypeColor = (type: string): string => {
                  const colorMap: { [key: string]: string } = {
                    es_deadline: "bg-red-100 text-red-800 border-red-200",
                    internship: "bg-blue-100 text-blue-800 border-blue-200",
                    interview:
                      "bg-orange-100 text-orange-800 border-orange-200",
                    screening:
                      "bg-purple-100 text-purple-800 border-purple-200",
                    orientation: "bg-teal-100 text-teal-800 border-teal-200",
                    other: "bg-gray-100 text-gray-800 border-gray-200",
                  };
                  return (
                    colorMap[type] ||
                    "bg-gray-100 text-gray-800 border-gray-200"
                  );
                };

                return (
                  <div
                    key={schedule.id}
                    className={`p-4 rounded-lg border-2 transition-all hover:shadow-md cursor-pointer ${
                      isToday ? "bg-yellow-50 border-yellow-400" : "bg-gray-50"
                    }`}
                    onClick={() => navigate("/schedules")}
                  >
                    <div className="flex items-start gap-3 flex-col sm:flex-row">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <span
                            className={`px-3 py-1 rounded-full text-sm font-medium border ${getScheduleTypeColor(
                              schedule.schedule_type
                            )}`}
                          >
                            {getScheduleTypeLabel(schedule.schedule_type)}
                          </span>
                          {isToday && (
                            <span className="px-3 py-1 rounded-full text-sm font-bold bg-yellow-400 text-yellow-900">
                              今日
                            </span>
                          )}
                          {isTomorrow && (
                            <span className="px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                              明日
                            </span>
                          )}
                        </div>
                        <h3 className="text-lg font-semibold text-gray-800 m-0 mb-1">
                          {schedule.company_name}
                        </h3>
                        <p className="text-sm text-gray-600 m-0">
                          {startDate.toLocaleString("ja-JP", {
                            month: "long",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </p>
                      </div>
                      <button
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate("/schedules");
                        }}
                      >
                        詳細を見る
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {upcomingSchedules.length > 0 && (
            <div className="mt-6 text-center">
              <button
                className="px-6 py-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors font-medium"
                onClick={() => navigate("/schedules")}
              >
                すべてのスケジュールを見る →
              </button>
            </div>
          )}
        </div>

        {/* ステータスごとの応募数内訳 */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">
            応募ステータス内訳
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">
                {statusCounts.applied}
              </div>
              <div className="text-sm text-gray-600 mt-1">応募済み</div>
            </div>
            <div className="text-center p-4 bg-yellow-50 rounded-lg">
              <div className="text-2xl font-bold text-yellow-600">
                {statusCounts.document_screening}
              </div>
              <div className="text-sm text-gray-600 mt-1">書類選考中</div>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <div className="text-2xl font-bold text-purple-600">
                {statusCounts.interview_scheduled}
              </div>
              <div className="text-sm text-gray-600 mt-1">面接予定</div>
            </div>
            <div className="text-center p-4 bg-orange-50 rounded-lg">
              <div className="text-2xl font-bold text-orange-600">
                {statusCounts.final_interview}
              </div>
              <div className="text-sm text-gray-600 mt-1">最終面接</div>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">
                {statusCounts.offer}
              </div>
              <div className="text-sm text-gray-600 mt-1">内定</div>
            </div>
            <div className="text-center p-4 bg-red-50 rounded-lg">
              <div className="text-2xl font-bold text-red-600">
                {statusCounts.rejected}
              </div>
              <div className="text-sm text-gray-600 mt-1">不合格</div>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-gray-600">
                {statusCounts.declined}
              </div>
              <div className="text-sm text-gray-600 mt-1">辞退</div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default DashboardPage;
